import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchWithSessionToken } from '../lib/appBridge';
import {
  ensureGoogleAnalytics,
  identifyAnalyticsUser,
  setAnalyticsTags,
  trackAnalyticsEvent,
  trackGooglePageView,
} from '../lib/analytics';
import { getClaritySessionId, normalizeShopDomain } from '../lib/clarity';

type ClarityTrackerProps = {
  shop: string;
  host: string;
};

type ShopMetadata = {
  shopName?: string;
  shopPlan?: string;
  primaryDomain?: string;
};

export default function ClarityTracker({ shop, host }: ClarityTrackerProps) {
  const location = useLocation();
  const lastRouteEventKey = useRef<string | null>(null);
  const appOpenedTrackedRef = useRef(false);
  const [metadata, setMetadata] = useState<ShopMetadata>({});

  const shopDomain = useMemo(() => normalizeShopDomain(shop), [shop]);
  const sessionId = useMemo(() => getClaritySessionId(), []);
  const environment = import.meta.env.MODE || (import.meta.env.DEV ? 'development' : 'production');

  useEffect(() => {
    if (!shopDomain || !host) return;

    ensureGoogleAnalytics({
      shopDomain,
      sessionId,
      environment,
    });
    setAnalyticsTags({
      appSurface: 'shopify-admin',
      environment,
      shopDomain,
    });

    if (!appOpenedTrackedRef.current) {
      trackAnalyticsEvent('app_opened', {
        appSurface: 'shopify-admin',
        environment,
        shopDomain,
        appSessionId: sessionId,
      });
      appOpenedTrackedRef.current = true;
    }
  }, [environment, host, sessionId, shopDomain]);

  useEffect(() => {
    if (!shopDomain || !host) return;

    let cancelled = false;

    const loadShopMetadata = async () => {
      try {
        const params = new URLSearchParams({ shop: shopDomain });
        params.set('host', host);
        const res = await fetchWithSessionToken(host, `/api/shop?${params.toString()}`);
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        const shopData = data?.data?.shop || data?.shop || {};
        const nextMetadata: ShopMetadata = {
          shopName: shopData?.name,
          shopPlan: shopData?.plan?.displayName,
          primaryDomain: shopData?.primaryDomain?.host || shopData?.primaryDomain?.url,
        };

        if (cancelled) return;
        setMetadata(nextMetadata);
        setAnalyticsTags({
          shopName: nextMetadata.shopName,
          shopPlan: nextMetadata.shopPlan,
          primaryDomain: nextMetadata.primaryDomain,
        });
      } catch {
        // Shop metadata is optional for analytics.
      }
    };

    void loadShopMetadata();

    return () => {
      cancelled = true;
    };
  }, [host, shopDomain]);

  useEffect(() => {
    if (!shopDomain) return;

    const route = location.pathname || '/';
    const routeKey = `${route}${location.search}`;
    const commonTags = {
      appSurface: 'shopify-admin',
      environment,
      route,
      shopDomain,
      shopName: metadata.shopName,
      shopPlan: metadata.shopPlan,
      primaryDomain: metadata.primaryDomain,
    };

    identifyAnalyticsUser({
      shopDomain,
      sessionId,
      pageId: route,
      friendlyName: metadata.shopName,
      environment,
      metadata: {
        shopName: metadata.shopName,
        shopPlan: metadata.shopPlan,
        primaryDomain: metadata.primaryDomain,
      },
    });
    setAnalyticsTags({
      ...commonTags,
      appSessionId: sessionId,
    });

    if (lastRouteEventKey.current !== routeKey) {
      trackGooglePageView({
        path: `${route}${location.search}`,
        tags: {
          ...commonTags,
          appSessionId: sessionId,
        },
      });
      trackAnalyticsEvent('route_view', {
        ...commonTags,
        appSessionId: sessionId,
      });
      lastRouteEventKey.current = routeKey;
    }
  }, [
    environment,
    location.pathname,
    location.search,
    metadata.primaryDomain,
    metadata.shopName,
    metadata.shopPlan,
    sessionId,
    shopDomain,
  ]);

  return null;
}
